import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { GoogleCredentialResponse, GsiButtonConfiguration } from '../../types/google-accounts';

const GIS_SCRIPT_SRC = 'https://accounts.google.com/gsi/client';

// Loads the Google Identity Services client once and resolves when ready.
// Concurrent callers share the same in-flight promise.
let gisScriptPromise: Promise<void> | null = null;

function loadGisScript(): Promise<void> {
  if (window.google?.accounts?.id) return Promise.resolve();
  if (gisScriptPromise) return gisScriptPromise;

  gisScriptPromise = new Promise<void>((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(`script[src="${GIS_SCRIPT_SRC}"]`);
    if (existing) {
      existing.addEventListener('load', () => resolve());
      existing.addEventListener('error', () => reject(new Error('Failed to load Google Identity Services')));
      return;
    }

    const script = document.createElement('script');
    script.src = GIS_SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => {
      gisScriptPromise = null;
      reject(new Error('Failed to load Google Identity Services'));
    };
    document.head.appendChild(script);
  });

  return gisScriptPromise;
}

interface GoogleSignInButtonProps {
  onCredential: (credential: string) => void | Promise<void>;
  text?: GsiButtonConfiguration['text'];
}

// Renders the official Google Sign-In button. Returns null (rendering nothing)
// when VITE_GOOGLE_CLIENT_ID is not configured, so the rest of the auth form
// keeps working in environments without Google SSO set up.
export function GoogleSignInButton({ onCredential, text = 'continue_with' }: GoogleSignInButtonProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const { i18n } = useTranslation();
  const [failed, setFailed] = useState(false);
  const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID as string | undefined;

  // GIS invokes the callback outside React, so keep the latest handler in a ref
  // to avoid re-initializing the button on every render.
  const onCredentialRef = useRef(onCredential);
  onCredentialRef.current = onCredential;

  const handleCredential = useCallback((response: GoogleCredentialResponse) => {
    if (response.credential) {
      void onCredentialRef.current(response.credential);
    }
  }, []);

  useEffect(() => {
    if (!clientId) return;

    let cancelled = false;

    loadGisScript()
      .then(() => {
        if (cancelled || !window.google?.accounts?.id || !containerRef.current) return;

        window.google.accounts.id.initialize({
          client_id: clientId,
          callback: handleCredential,
        });

        window.google.accounts.id.renderButton(containerRef.current, {
          type: 'standard',
          theme: 'outline',
          size: 'large',
          text,
          shape: 'rectangular',
          logo_alignment: 'left',
          width: 320,
          locale: i18n.language,
        });
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });

    return () => {
      cancelled = true;
    };
  }, [clientId, handleCredential, text, i18n.language]);

  if (!clientId || failed) return null;

  return <div ref={containerRef} className="flex w-full justify-center" />;
}
